/** @format */

import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  Fade,
} from "@mui/material";
import { Box } from "./MuiCompat";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getMainGradient } from "./utils";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    paddingTop: theme.spacing(25),
    paddingBottom: theme.spacing(8),
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(10),
  },
  title: {
    fontWeight: "500 !important",
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
  },
  subtitle: {
    fontWeight: 300,
    fontSize: "1.5rem !important",
    paddingTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
  },
  benefitsText: {
    fontWeight: 300,
    fontSize: "1.3rem !important",
    paddingTop: theme.spacing(6),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
  },
  form: {
    marginTop: theme.spacing(8),
    width: "100%",
    maxWidth: "600px",
  },
  input: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      // backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderRadius: "4px",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.5)", // Added border color for unfocused state
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.9)", // White with opacity 0.9 for hover state
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "white",
    },
    "& .MuiInputBase-input": {
      color: "white",
    },
  },

  submitButton: {
    width: "24%",
    borderRadius: "55px !important",
    backgroundColor: "rgba(216, 216, 216, 1) !important",
    boxShadow: "none !important",
    color: "#fff",
    transition: "all 0.3s ease-in !important",
    "&:not([disabled])": {
      background: `${getMainGradient()} !important`,
    },
    "&:hover": {
      background: `${getMainGradient()} !important`,
    },
    "&:not([disabled]):hover": {
      "& .MuiButton-root": {
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        transition: "text-decoration 0.3s ease-in",
      },
    },
  },
  successContainer: {
    display: "flex",
    flex: 1,
    minHeight: "50vh",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "700px",
    padding: theme.spacing(4),
  },
  successIcon: {
    fontSize: "60px !important",
    margin: theme.spacing(2, 0, 4),
    color: "#fff",
  },
  successTitle: {
    fontWeight: "500 !important",
    fontSize: "2.2rem !important",
    marginBottom: theme.spacing(3),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
  },
  successMessage: {
    fontWeight: 300,
    fontSize: "1.2rem !important",
    marginBottom: theme.spacing(2),
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif !important",
    maxWidth: "600px",
    lineHeight: 1.6,
  },
}));

const pilotMessage = (
  <>
    Be the first to experience the future of healthy longevity with our beta
    App.
    <br />
    Join the waitlist now for exclusive early access!
  </>
);
const contactMessage =
  "By analyzing and integrating your health information holistically, our algorithm assesses and suggests the best way to enhance your longevity.";

const pilotExtraMessage = [
  "Be the first to experience new features",
  "Unlock exclusive deals",
  "Influence the Future with Your Feedback",
  "👉 Secure Your Spot Today!",
];

const ContactPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const initialFormState = {
    name: "",
    email: "",
    message: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  // Function to check if name and email fields are filled
  const isFormValid = () => {
    const isValidEmail =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(
        formData.email.trim()
      );
    return formData.name.trim() !== "" && isValidEmail;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
      isPilot: false,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.eon.health/contact_form", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmissionSuccess(true);
        resetForm();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to send message. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "An unexpected error occurred. Please try again later.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success component that will replace the form
  const SuccessView = () => (
    <Fade in={submissionSuccess} timeout={800}>
      <Box className={classes.successContainer}>
        <CheckCircleIcon className={classes.successIcon} />

        <Typography variant="h2" className={classes.successTitle}>
          Thank You for Reaching Out!
        </Typography>

        <Typography variant="body2" className={classes.successMessage}>
          We've received your message and will get back to you soon. Our team is
          committed to providing you with the best possible response.
        </Typography>
      </Box>
    </Fade>
  );

  const isFromPilotTest = location.state?.from === "PilotTest";

  return (
    <Box className={classes.root}>
      <Container maxWidth="lg">
        <Box className={classes.content}>
          {submissionSuccess ? (
            <SuccessView />
          ) : (
            <>
              <Typography variant="h2" className={classes.title}>
                {isFromPilotTest ? "🚀 Get Early Access!" : "Contact Us"}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {isFromPilotTest ? pilotMessage : contactMessage}
              </Typography>

              {isFromPilotTest && (
                <Typography variant="body1" className={classes.benefitsText}>
                  {pilotExtraMessage.map((message, index) => (
                    <Box key={index} display="flex" alignItems="center">
                      {index < pilotExtraMessage.length - 1 && (
                        <Box
                          component="span"
                          display="inline-block"
                          sx={{
                            color: "green",
                            marginRight: "8px",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </Box>
                      )}
                      {message}
                    </Box>
                  ))}
                </Typography>
              )}

              <form onSubmit={handleSubmit} className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={classes.input}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={classes.input}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      className={classes.input}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        className={classes.submitButton}
                        disabled={isSubmitting || !isFormValid()}
                        sx={{
                          position: "relative",
                          "&:not([disabled])": {
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              width: "0%",
                              height: "1px",
                              bottom: "6px",
                              left: "50%",
                              backgroundColor: "#fff",
                              transform: "translateX(-50%)",
                              transition: "width 0.3s ease-in",
                            },
                            "&:hover::after": {
                              width: "70%",
                            },
                          },
                        }}
                      >
                        {isFromPilotTest
                          ? isSubmitting
                            ? "Joining..."
                            : "Join Waitlist"
                          : isSubmitting
                          ? "Sending..."
                          : "Send Email"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </>
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactPage;
